import React, { useState } from 'react';
import {
  Archive, ArchiveRestore, BookOpen, Calendar, Check, Clock, Edit3, FolderPlus,
  Gamepad2, History, ListPlus, MessageSquare, Percent, Play, RotateCcw, Star,
  Tag, Trophy, X
} from 'lucide-react';
import { STATUSES } from '../data/constants';
import { playedHoursLabel } from '../utils/gameUtils';
import { useModalA11y } from '../hooks/useModalA11y';

const QUICK_ACTIONS = [
  { status: 'playing', label: 'Começar a jogar', icon: Play },
  { status: 'completed', label: 'Marcar como finalizado', icon: Check },
  { status: 'backlog', label: 'Mover para o backlog', icon: ListPlus },
  { status: 'dropped', label: 'Marcar como abandonado', icon: X }
];

const GameDetailsModal = ({
  game,
  onClose,
  onEdit,
  onQuickStatus,
  onToggleFavorite,
  onToggleArchive,
  onAddJournalEntry,
  onRegisterCompletion,
  onToggleQueue,
  isQueued,
  queueFull,
  lists = [],
  onToggleList,
  onCreateList
}) => {
  const containerRef = useModalA11y(onClose);
  const [journalText, setJournalText] = useState('');
  const [newListName, setNewListName] = useState('');
  const status = STATUSES.find(item => item.value === game.status) || STATUSES[0];
  const date = game.dateFinished
    ? new Date(`${game.dateFinished}T12:00:00`).toLocaleDateString('pt-BR')
    : 'Não informada';
  const playtime = playedHoursLabel(game);
  const history = Array.isArray(game.statusHistory) ? [...game.statusHistory].reverse() : [];
  const completions = Array.isArray(game.completions) ? [...game.completions].reverse() : [];
  const journal = Array.isArray(game.journal) ? [...game.journal].reverse() : [];

  const submitJournal = () => {
    if (!journalText.trim()) return;
    onAddJournalEntry(game, journalText.trim());
    setJournalText('');
  };

  const submitNewList = () => {
    if (!newListName.trim()) return;
    onCreateList(newListName.trim());
    setNewListName('');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-end sm:items-center sm:justify-center sm:p-5" role="dialog" aria-modal="true" aria-label={`Detalhes de ${game.name}`}>
      <button className="absolute inset-0 w-full cursor-default" onClick={onClose} aria-label="Fechar detalhes" />
      <article ref={containerRef} tabIndex={-1} className="relative w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto bg-[#11141c] sm:rounded-[30px] rounded-t-[30px] border border-white/[0.09] animate-slideUp">
        <div className="relative h-56 sm:h-72 overflow-hidden">
          {game.coverImage ? <>
            <img src={game.coverImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-55" />
            <img src={game.coverImage} alt={`Imagem de ${game.name}`} className="relative w-full h-full object-contain" />
          </> : <div className="w-full h-full grid place-items-center bg-[#171b24]"><Gamepad2 className="w-12 h-12 text-slate-700" /></div>}
          <div className="absolute inset-0 bg-gradient-to-t from-[#11141c] via-transparent to-black/30" />
          <button onClick={onClose} className="icon-button absolute top-4 right-4 z-10" aria-label="Fechar"><X className="w-4 h-4" /></button>
          <button
            onClick={() => onToggleFavorite(game)}
            className={`icon-button absolute top-4 left-4 z-10 ${game.favorite ? '!bg-amber-400 !text-black' : ''}`}
            aria-label={game.favorite ? 'Remover dos favoritos' : 'Marcar como favorito'}
            aria-pressed={!!game.favorite}
          >
            <Star className={`w-4 h-4 ${game.favorite ? 'fill-current' : ''}`} />
          </button>
        </div>

        <div className="relative -mt-12 p-5 sm:p-8 pt-0">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                <span className={`badge ${status.color}`}>{status.label}</span>
                <span className="badge bg-white/[0.07] text-slate-300">{game.platform}</span>
                {game.platinado && <span className="badge bg-lime-400 text-black flex items-center gap-1"><Trophy className="w-3 h-3" /> 100%</span>}
                {game.archived && <span className="badge bg-white/[0.07] text-slate-400 flex items-center gap-1"><Archive className="w-3 h-3" /> Arquivado</span>}
              </div>
              <h2 className="text-2xl sm:text-4xl font-extrabold tracking-[-.04em] leading-tight">{game.name}</h2>
            </div>
            <button onClick={() => onEdit(game)} className="btn-primary px-4 py-3 text-xs flex items-center gap-2 shrink-0"><Edit3 className="w-4 h-4" /><span className="hidden sm:inline">Editar jogo</span></button>
          </div>

          <section className="mt-6" aria-label="Ações rápidas de status">
            <h3 className="text-xs font-extrabold uppercase tracking-[.15em] text-slate-500 mb-3">Ações rápidas</h3>
            <div className="flex flex-wrap gap-2">
              {QUICK_ACTIONS.map(({ status: value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => onQuickStatus(game, value)}
                  disabled={game.status === value}
                  className={`px-3.5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition ${
                    game.status === value ? 'bg-white/[0.06] text-slate-600 cursor-default' : 'btn-secondary'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
              <button onClick={() => onToggleArchive(game)} className="px-3.5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 btn-secondary">
                {game.archived ? <><ArchiveRestore className="w-3.5 h-3.5" /> Desarquivar</> : <><Archive className="w-3.5 h-3.5" /> Arquivar</>}
              </button>
              {game.status === 'backlog' && (
                <button
                  onClick={() => onToggleQueue(game)}
                  disabled={!isQueued && queueFull}
                  className="px-3.5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 btn-secondary disabled:opacity-40"
                >
                  <ListPlus className="w-3.5 h-3.5" /> {isQueued ? 'Remover da fila' : queueFull ? 'Fila cheia (5)' : 'Jogar em seguida'}
                </button>
              )}
            </div>
          </section>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-7">
            <Info icon={Star} label="Sua nota" value={`${game.rating || 0} / 5`} accent />
            <Info icon={Calendar} label="Finalização" value={date} />
            <Info icon={Tag} label="Gêneros" value={game.tags?.length ? game.tags.slice(0,2).join(', ') : 'Não informado'} />
            {playtime && <Info icon={Clock} label="Tempo jogado" value={playtime} />}
            {!!game.progressPercent && <Info icon={Percent} label="Progresso" value={`${game.progressPercent}%`} />}
          </div>

          {!!game.progressPercent && (
            <div className="mt-4">
              <div className="progress-track"><div className="progress-fill" style={{ width: `${game.progressPercent}%` }} /></div>
            </div>
          )}

          <section className="mt-7 border-t border-white/[0.07] pt-6">
            <h3 className="text-xs font-extrabold uppercase tracking-[.15em] text-slate-500 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Suas impressões</h3>
            <p className="text-sm text-slate-300 leading-relaxed mt-3 whitespace-pre-wrap">{game.notes?.trim() || 'Você ainda não escreveu nada sobre este jogo.'}</p>
          </section>

          <section className="mt-7 border-t border-white/[0.07] pt-6">
            <h3 className="text-xs font-extrabold uppercase tracking-[.15em] text-slate-500 flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Rejogadas</h3>
            <p className="text-sm text-slate-400 mt-2">{completions.length > 0 ? `Você já finalizou este jogo ${completions.length} ${completions.length === 1 ? 'vez' : 'vezes'}.` : 'Ainda sem finalizações registradas.'}</p>
            {completions.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {completions.map((entry, index) => (
                  <li key={index} className="text-xs text-slate-500 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-lime-400" /> {new Date(entry.date).toLocaleDateString('pt-BR')}</li>
                ))}
              </ul>
            )}
            <button onClick={() => onRegisterCompletion(game)} className="btn-secondary px-4 py-2.5 text-xs mt-4 flex items-center gap-2"><RotateCcw className="w-3.5 h-3.5" /> Registrar nova finalização</button>
          </section>

          <section className="mt-7 border-t border-white/[0.07] pt-6">
            <h3 className="text-xs font-extrabold uppercase tracking-[.15em] text-slate-500 flex items-center gap-2"><BookOpen className="w-4 h-4" /> Diário de jornada</h3>
            {journal.length > 0 ? (
              <ul className="mt-3 space-y-3">
                {journal.map((entry, index) => (
                  <li key={index} className="text-sm">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{new Date(entry.date).toLocaleDateString('pt-BR')}</span>
                    <p className="text-slate-300 mt-0.5">{entry.text}</p>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-slate-500 mt-2">Registre pequenos momentos da sua jornada com este jogo.</p>}
            <div className="flex gap-2 mt-4">
              <input
                value={journalText}
                onChange={e => setJournalText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submitJournal()}
                className="input-field"
                placeholder='Ex: "Derrotei o primeiro chefe."'
                aria-label="Nova anotação do diário"
              />
              <button onClick={submitJournal} className="btn-secondary px-4 text-xs shrink-0">Salvar</button>
            </div>
          </section>

          <section className="mt-7 border-t border-white/[0.07] pt-6">
            <h3 className="text-xs font-extrabold uppercase tracking-[.15em] text-slate-500 flex items-center gap-2"><FolderPlus className="w-4 h-4" /> Coleções</h3>
            <div className="flex flex-wrap gap-2 mt-3">
              {lists.map(list => {
                const active = list.gameIds.includes(game.id);
                return (
                  <button
                    key={list.id}
                    onClick={() => onToggleList(list.id, game.id)}
                    aria-pressed={active}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${active ? 'bg-violet-600 text-white' : 'bg-white/[0.05] text-slate-400 hover:text-white'}`}
                  >
                    {list.name}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 mt-3">
              <input value={newListName} onChange={e => setNewListName(e.target.value)} onKeyDown={e => e.key === 'Enter' && submitNewList()} className="input-field" placeholder="Nova coleção (ex: Melhores RPGs)" aria-label="Nome da nova coleção" />
              <button onClick={submitNewList} className="btn-secondary px-4 text-xs shrink-0">Criar</button>
            </div>
          </section>

          {history.length > 0 && (
            <section className="mt-7 border-t border-white/[0.07] pt-6">
              <h3 className="text-xs font-extrabold uppercase tracking-[.15em] text-slate-500 flex items-center gap-2"><History className="w-4 h-4" /> Histórico de status</h3>
              <ul className="mt-3 space-y-1.5">
                {history.map((entry, index) => {
                  const fromLabel = STATUSES.find(s => s.value === entry.from)?.label || 'Novo jogo';
                  const toLabel = STATUSES.find(s => s.value === entry.to)?.label || entry.to;
                  return <li key={index} className="text-xs text-slate-500">{fromLabel} → <span className="text-slate-300 font-semibold">{toLabel}</span> · {new Date(entry.date).toLocaleDateString('pt-BR')}</li>;
                })}
              </ul>
            </section>
          )}
        </div>
      </article>
    </div>
  );
};

const Info = ({ icon:Icon, label, value, accent, className='' }) => <div className={`rounded-2xl bg-white/[0.035] border border-white/[0.07] p-4 ${className}`}><Icon className={`w-4 h-4 mb-3 ${accent ? 'text-amber-300 fill-amber-300' : 'text-lime-400'}`} /><div className="text-[9px] font-extrabold uppercase tracking-wider text-slate-600">{label}</div><div className="text-xs font-bold text-slate-200 mt-1 truncate">{value}</div></div>;
export default GameDetailsModal;
